// utility/location-tree/location-tree.component.ts
// ════════════════════════════════════════════════════════════════════════════
// P-LOC · LOC-2 / LOC-3 — ONE location tree, for every screen that picks a location.
//
// THE BUG IT EXISTS TO KILL
//   The old selector (user/locations/location-tree-selector) kept a FLAT Set of loc_ids.
//   Tick "Bihar" and it showed Bihar ticked and every district under it unticked. The
//   backend never read it that way: every enforcement query (location.js orgScopeCTE,
//   data-entry.js:290-301) treats a ticked node as a SEED and expands it RECURSIVELY
//   DOWNWARD. So "Bihar" silently granted ~45,000 villages. The checkbox was never wrong
//   — the screen was. One tick, two meanings.
//
// WHAT THIS ONE DOES
//   • A ticked node is a seed, and (in 'scope' mode) it says which kind:
//       ROOT — this node AND everything beneath it   (the default; the common case)
//       NODE — this node ONLY
//     Both already exist in vd_location_scope_org.scope_type; there was simply never a UI.
//   • Descendants of a ROOT seed render as INHERITED — ticked, dimmed, not individually
//     removable, because the engine cannot express "all of Bihar except this village".
//   • A seed inside a COLLAPSED branch puts a partial mark (—) on its ancestors, so a
//     selection can never hide in a folded branch.
//   • Children are PAGED. A block holds thousands of villages; a branch must be walkable
//     without ever being loaded whole.
//
// IT OWNS NO API. The caller passes a `source` (see LocationTreeSource): vadmin passes one
// backed by /api/location-admin (global master), the org screens pass one backed by
// /api/location (scoped to their org, or to one project). That is the only reason one
// component can serve both — and the reason there is only one.
//
// Lives in UtilityModule because user.module and vadmin.module BOTH already import it.
// Two tree components would drift apart, which is the exact disease this file cures.
// ════════════════════════════════════════════════════════════════════════════
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

export type ScopeType = 'ROOT' | 'NODE';

export interface ScopeItem {
  loc_id: number;
  scope_type: ScopeType;
}

export interface LocationTreeNodeData {
  loc_id: number;
  parent_id: number | null;
  country_id: number;
  layer_level: number;
  location_name: string;
  location_code?: string | null;
  location_name_local?: string | null;
  census_code?: string | null;
  lgd_code?: string | null;
  postal_code?: string | null;
  is_active: number;
  layer_name?: string | null;
  is_leaf?: number;
  has_children?: number;
}

/**
 * Everything the tree needs from a backend, and nothing more.
 * Every method resolves the codebase's usual `{ status, data }` envelope.
 *   children  → data: { rows, total, has_more }
 *   search    → data: LocationTreeNodeData[]
 *   breadcrumb→ data: [{ loc_id, location_name, layer_level }]  (root first)
 *   ancestors → data: { ancestor_ids: number[] }
 */
export interface LocationTreeSource {
  children(p: { parent_id: number | null; offset: number; limit: number }): Promise<any>;
  search(p: { q: string; limit?: number }): Promise<any>;
  breadcrumb(loc_id: number): Promise<any>;
  ancestors(loc_ids: number[]): Promise<any>;
}

export interface TreeNode extends LocationTreeNodeData {
  children: TreeNode[];
  parent: TreeNode | null;
  loaded: boolean;
  loading: boolean;
  expanded: boolean;
  childTotal: number;
  childHasMore: boolean;
}

/** Deliberately small: the point is never to hold a whole branch in memory. */
const PAGE = 200;

@Component({
  selector: 'app-location-tree',
  templateUrl: './location-tree.component.html',
  styleUrls: ['./location-tree.component.scss'],
})
export class LocationTreeComponent implements OnChanges {

  /** Where the nodes come from. Changing it reloads from the top. */
  @Input() source: LocationTreeSource | null = null;

  /**
   * 'scope'  → ticks are seeds AND carry a ROOT/NODE choice.
   *            Only `vd_location_scope_org` has a `scope_type` column, so only the vadmin
   *            org-scope screen may use this.
   *
   * 'seed'   → ticks are seeds and ALWAYS carry their subtree, with no choice offered.
   *            This is the truth for `vd_location_project`, `vd_location_user` and
   *            `vd_location_user_project`: none of them has a scope_type column, and the
   *            enforcement CTEs (data-entry.js `proj_exp` / `user_exp`) expand every one of
   *            their rows downward unconditionally. Ticking a district there ALREADY grants
   *            its villages — showing a ROOT/NODE switch would imply a choice the schema
   *            cannot store.
   *
   * 'browse' → highlight one node, no ticks       (the location master)
   */
  @Input() mode: 'scope' | 'seed' | 'browse' = 'scope';

  /** scope/select mode — current seeds. Two-way. */
  @Input() seeds: ScopeItem[] = [];
  @Output() seedsChange = new EventEmitter<ScopeItem[]>();

  /** browse mode — the highlighted node. */
  @Input() selectedLocId: number | null = null;
  @Output() selectedChange = new EventEmitter<TreeNode | null>();

  /** Bump to force a reload (e.g. after creating a location). */
  @Input() refreshKey = 0;

  /** Shown when the tree has nothing in it — the caller knows why better than we do. */
  @Input() emptyText = 'Nothing to show here.';

  roots: TreeNode[] = [];
  rootTotal = 0;
  rootHasMore = false;

  loading = false;
  loadingMore = false;

  search = '';
  searching = false;
  searchResults: LocationTreeNodeData[] = [];

  private seedMap = new Map<number, ScopeType>();
  private ancestorIds = new Set<number>();

  /**
   * The array we last emitted. The parent stores it straight back into [seeds], which
   * re-triggers ngOnChanges — without this we would rebuild the map and re-fetch ancestors
   * a second time on every single tick.
   */
  private lastEmitted: ScopeItem[] | null = null;

  constructor(private toastr: ToastrService) {}

  async ngOnChanges(ch: SimpleChanges): Promise<void> {
    if (ch['seeds'] && this.seeds !== this.lastEmitted) {
      this.seedMap = new Map((this.seeds || []).map(s => [Number(s.loc_id), s.scope_type]));
      await this.refreshAncestors();
    }
    if (ch['source'] || ch['refreshKey']) {
      await this.loadRoots();
    }
  }

  /* ── loading ── */

  private toNode(r: any, parent: TreeNode | null): TreeNode {
    return {
      loc_id: Number(r.loc_id),
      parent_id: r.parent_id == null ? null : Number(r.parent_id),
      country_id: Number(r.country_id),
      layer_level: Number(r.layer_level),
      location_name: r.location_name,
      location_code: r.location_code ?? null,
      location_name_local: r.location_name_local ?? null,
      census_code: r.census_code ?? null,
      lgd_code: r.lgd_code ?? null,
      postal_code: r.postal_code ?? null,
      is_active: Number(r.is_active ?? 1),
      layer_name: r.layer_name ?? null,
      is_leaf: Number(r.is_leaf ?? 0),
      has_children: Number(r.has_children ?? 0),
      children: [],
      parent,
      loaded: false,
      loading: false,
      expanded: false,
      childTotal: 0,
      childHasMore: false,
    };
  }

  private async fetchPage(parent: TreeNode | null, offset: number): Promise<void> {
    if (!this.source) return;

    const res: any = await this.source.children({
      parent_id: parent ? parent.loc_id : null,
      offset,
      limit: PAGE,
    });
    if (Number(res?.status) !== 1) {
      this.toastr.error(res?.msg || 'Could not load locations');
      return;
    }

    const rows = (res.data?.rows || []).map((r: any) => this.toNode(r, parent));
    const total = Number(res.data?.total || 0);
    const hasMore = !!res.data?.has_more;

    if (parent) {
      parent.children = offset === 0 ? rows : parent.children.concat(rows);
      parent.childTotal = total;
      parent.childHasMore = hasMore;
      parent.loaded = true;
    } else {
      this.roots = offset === 0 ? rows : this.roots.concat(rows);
      this.rootTotal = total;
      this.rootHasMore = hasMore;
    }
  }

  async loadRoots(): Promise<void> {
    this.roots = [];
    this.rootTotal = 0;
    this.rootHasMore = false;
    if (!this.source) return;

    this.loading = true;
    try {
      await this.fetchPage(null, 0);
    } catch (e: any) {
      this.toastr.error(e?.message || 'Could not load locations');
    } finally {
      this.loading = false;
    }
  }

  async toggleExpand(n: TreeNode): Promise<void> {
    if (!n.has_children) return;
    if (n.expanded) { n.expanded = false; return; }
    n.expanded = true;
    if (n.loaded) return;

    n.loading = true;
    try {
      await this.fetchPage(n, 0);
    } catch (e: any) {
      this.toastr.error(e?.message || 'Could not load children');
    } finally {
      n.loading = false;
    }
  }

  async loadMore(n: TreeNode | null, ev?: Event): Promise<void> {
    ev?.stopPropagation();
    this.loadingMore = true;
    try {
      await this.fetchPage(n, n ? n.children.length : this.roots.length);
    } catch (e: any) {
      this.toastr.error(e?.message || 'Could not load more');
    } finally {
      this.loadingMore = false;
    }
  }

  remaining(n: TreeNode | null): number {
    return n
      ? Math.max(n.childTotal - n.children.length, 0)
      : Math.max(this.rootTotal - this.roots.length, 0);
  }

  async reload(n: TreeNode | null): Promise<void> {
    if (!n) { await this.loadRoots(); return; }
    n.loaded = false;
    n.expanded = false;
    n.children = [];
    await this.toggleExpand(n);
  }

  /* ── seed state ── */

  get ticking(): boolean {
    return this.mode === 'scope' || this.mode === 'seed';
  }

  /**
   * Every ticking mode grants subtrees, because every one of these tables is expanded
   * downward by the engine. The modes differ only in whether that expansion is OPTIONAL
   * (scope → ROOT/NODE) or unconditional (seed → always the subtree).
   */
  get subtreeAware(): boolean {
    return this.ticking;
  }

  /** The ROOT/NODE switch is shown only where the schema can actually store the answer. */
  get offersScopeChoice(): boolean {
    return this.mode === 'scope';
  }

  isSeed(n: TreeNode): boolean {
    return this.seedMap.has(n.loc_id);
  }

  scopeOf(n: TreeNode): ScopeType | null {
    return this.seedMap.get(n.loc_id) ?? null;
  }

  /**
   * Covered by an ancestor seeded as ROOT. It cannot be ticked or unticked on its own:
   * the backend expands ROOT downward unconditionally, so offering a choice here would be
   * offering something the engine cannot honour.
   */
  isInherited(n: TreeNode): boolean {
    if (!this.subtreeAware) return false;
    let p = n.parent;
    while (p) {
      if (this.seedMap.get(p.loc_id) === 'ROOT') return true;
      p = p.parent;
    }
    return false;
  }

  isPartial(n: TreeNode): boolean {
    if (this.isSeed(n) || this.isInherited(n)) return false;
    return this.ancestorIds.has(n.loc_id);
  }

  isChecked(n: TreeNode): boolean {
    return this.isSeed(n) || this.isInherited(n);
  }

  private async refreshAncestors(): Promise<void> {
    const ids = Array.from(this.seedMap.keys());
    if (!ids.length || !this.source) { this.ancestorIds = new Set(); return; }
    try {
      const res: any = await this.source.ancestors(ids);
      if (Number(res?.status) === 1) {
        this.ancestorIds = new Set<number>((res.data?.ancestor_ids || []).map((x: any) => Number(x)));
      }
    } catch {
      // Partial marks are a hint, not a guarantee — a failure here must not block the tree.
      this.ancestorIds = new Set();
    }
  }

  private emit(): void {
    const items: ScopeItem[] = Array.from(this.seedMap.entries())
      .map(([loc_id, scope_type]) => ({ loc_id, scope_type }));
    this.seeds = items;
    this.lastEmitted = items;
    this.seedsChange.emit(items);
    void this.refreshAncestors();
  }

  /* ── interaction ── */

  onNodeClick(n: TreeNode): void {
    if (this.mode !== 'browse') return;
    this.selectedLocId = n.loc_id;
    this.selectedChange.emit(n);
  }

  toggleSeed(n: TreeNode): void {
    if (!this.ticking) return;

    if (this.isInherited(n)) {
      this.toastr.info(
        `It is already covered by "${this.rootAncestorName(n)}", which was given with everything below it. ` +
        `Switch that entry to "this only" if you need to pick individually.`,
        'Inherited'
      );
      return;
    }
    if (n.is_active !== 1) {
      this.toastr.warning('This location is deactivated and cannot be assigned.');
      return;
    }

    if (this.seedMap.has(n.loc_id)) {
      this.seedMap.delete(n.loc_id);
    } else {
      // ROOT everywhere: in 'seed' mode it is the only thing the schema can mean, and in
      // 'scope' mode it is what an admin almost always intends — "give them this district"
      // means the villages in it, not an empty district.
      this.seedMap.set(n.loc_id, 'ROOT');
      this.dropDescendantSeeds(n);
    }
    this.emit();
  }

  setScope(n: TreeNode, scope_type: ScopeType, ev?: Event): void {
    ev?.stopPropagation();
    if (!this.offersScopeChoice || !this.seedMap.has(n.loc_id)) return;
    this.seedMap.set(n.loc_id, scope_type);
    if (scope_type === 'ROOT') this.dropDescendantSeeds(n);
    this.emit();
  }

  /**
   * Only walks what is LOADED — with paging, a descendant in an unopened page cannot be
   * reached from here. The backend de-duplicates for real (locationAdminRoute dedupeSeeds);
   * this is just so the screen agrees with itself immediately.
   */
  private dropDescendantSeeds(n: TreeNode): void {
    const walk = (node: TreeNode) => {
      for (const c of node.children || []) {
        this.seedMap.delete(c.loc_id);
        walk(c);
      }
    };
    walk(n);
  }

  private rootAncestorName(n: TreeNode): string {
    let p = n.parent;
    while (p) {
      if (this.seedMap.get(p.loc_id) === 'ROOT') return p.location_name;
      p = p.parent;
    }
    return 'a parent location';
  }

  /* ── search ── */

  async runSearch(): Promise<void> {
    const q = (this.search || '').trim();
    if (q.length < 2 || !this.source) { this.searchResults = []; return; }
    this.searching = true;
    try {
      const res: any = await this.source.search({ q, limit: 50 });
      this.searchResults = Number(res?.status) === 1 ? (res.data || []) : [];
    } catch {
      this.searchResults = [];
    } finally {
      this.searching = false;
    }
  }

  clearSearch(): void {
    this.search = '';
    this.searchResults = [];
  }

  async revealFromSearch(hit: LocationTreeNodeData): Promise<void> {
    await this.revealLocId(hit.loc_id);
    this.clearSearch();
  }

  /**
   * Open the tree ON a location: walk its breadcrumb down from the root, expanding as we go, so
   * it lands in its real place with its parents visible.
   *
   * A flat search hit — or a row clicked in a list view — tells you nothing about what it belongs
   * to. "Rampur" is meaningless until you can see which block it is in.
   *
   * Public: callers (org-locations' list view, search) drive this from outside.
   */
  async revealLocId(loc_id: number): Promise<void> {
    const target = Number(loc_id || 0);
    if (!target || !this.source) return;

    try {
      const res: any = await this.source.breadcrumb(target);
      if (Number(res?.status) !== 1) return;
      const path: any[] = res.data || [];

      if (!this.roots.length) await this.loadRoots();

      let level: TreeNode[] = this.roots;
      let node: TreeNode | null = null;

      for (const step of path) {
        const id = Number(step.loc_id);
        const found = level.find(x => x.loc_id === id) || null;
        // The breadcrumb may start ABOVE the scope's own roots — an org seeded at district level
        // still has a state above it, and we do not render that. Skip what we do not show.
        if (!found) continue;
        node = found;
        if (node.loc_id === target) break;
        if (!node.expanded || !node.loaded) await this.toggleExpand(node);
        level = node.children;
      }

      if (node) {
        this.selectedLocId = node.loc_id;
        if (this.mode === 'browse') this.selectedChange.emit(node);
      }
    } catch {
      this.toastr.error('Could not open that location in the tree');
    }
  }

  /** Clear the browse highlight. */
  clearSelection(): void {
    this.selectedLocId = null;
    if (this.mode === 'browse') this.selectedChange.emit(null);
  }

  trackById = (_: number, n: TreeNode) => n.loc_id;
}
